import React, { useState, useEffect } from 'react';
import { Building2, CheckCircle2, AlertCircle, RefreshCw, Plus, Users, Crown, Sparkles, LogOut, Wifi, WifiOff, Archive, Trash2 } from 'lucide-react';
import { 
  supabase, 
  fetchCompanies, 
  linkUserToCompany, 
  isMagicAdmin,
  testDatabaseConnection,
  updateCompany,
  type Company 
} from '../lib/supabase';
import { CompanyOnboarding } from './CompanyOnboarding';

interface CompanySelectorProps {
  onCompanySelected: (company: Company) => void;
}

export function CompanySelector({ onCompanySelected }: CompanySelectorProps) {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'unknown' | 'connected' | 'disconnected'>('unknown');
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [isMagicAdminUser, setIsMagicAdminUser] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null);
  const [showArchivedCompanies, setShowArchivedCompanies] = useState(false);
  const [isDeletingCompany, setIsDeletingCompany] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setConnectionStatus('unknown');

      console.log('Starting data load process...');

      // Test database connection first
      console.log('Testing database connection...');
      const connectionTest = await testDatabaseConnection();
      
      if (!connectionTest.success) {
        console.error('Database connection failed:', connectionTest);
        setConnectionStatus('disconnected');
        setError(`Connection failed: ${connectionTest.error}`);
        
        // Provide specific troubleshooting steps
        if (connectionTest.details?.networkIssue) {
          setError('Cannot reach Supabase servers. Please check:\n• Your internet connection\n• Supabase project status\n• Firewall or VPN settings');
        } else if (connectionTest.details?.timeout) {
          setError('Connection timeout. The Supabase server is not responding. Please try again or check your network connection.');
        } else {
          setError(`Database connection error: ${connectionTest.error}\n\nPlease check your Supabase configuration and project status.`);
        }
        return;
      }

      setConnectionStatus('connected');
      console.log('Database connection successful');

      // Get current user with better error handling
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      console.log('Auth user check:', { user, userError });
      
      if (userError) {
        console.error('User error:', userError);
        setError('Authentication error. Please log in again.');
        return;
      }
      
      if (!user) {
        console.log('No user found');
        setError('No authenticated user found. Please log in again.');
        return;
      }
      
      setCurrentUser(user);

      // Check if user is magic admin using the helper function
      console.log('Checking magic admin status for:', user.email);
      
      const isMagicAdminResult = await isMagicAdmin();
      setIsMagicAdminUser(isMagicAdminResult);

      // Load companies
      console.log('Loading companies...');
      const companiesData = await fetchCompanies();
      setCompanies(companiesData);
      
      console.log('Data loading complete:', {
        userEmail: user.email,
        isMagicAdmin: isMagicAdminResult,
        companiesCount: companiesData.length
      });
      
      setHasInitialized(true);
    } catch (err: any) {
      console.error('Error loading data:', err);
      setConnectionStatus('disconnected');
      
      // Provide more specific error messages
      if (err.message?.includes('Failed to fetch')) {
        setError('Network error: Cannot connect to Supabase. Please check your internet connection and try again.');
      } else if (err.message?.includes('Database connection failed')) {
        setError(err.message);
      } else {
        setError(`Failed to load data: ${err.message || 'Unknown error'}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompanySelect = async (company: Company) => {
    if (!currentUser) {
      setError('No authenticated user found');
      return;
    }

    try {
      setIsConnecting(true);
      setError(null);
      setSelectedCompanyId(company.id);

      console.log('Connecting user to company:', { userId: currentUser.id, companyId: company.id });

      // Link user to selected company
      const success = await linkUserToCompany(currentUser.id, company.id);
      
      if (!success) {
        setError('Failed to connect to company');
        return;
      }

      // Fetch the complete company data with features
      const { data: fullCompany, error: fetchError } = await supabase
        .from('companies')
        .select(`
          *,
          features:company_features(*)
        `)
        .eq('id', company.id)
        .single();

      if (fetchError || !fullCompany) {
        setError('Failed to load company details');
        return;
      }

      console.log('Successfully connected to company:', fullCompany);
      
      // Add a small delay to show the animation
      setTimeout(() => {
        onCompanySelected(fullCompany);
      }, 800);
    } catch (error) {
      console.error('Error connecting to company:', error);
      setError('Failed to connect to company');
      setSelectedCompanyId(null);
    } finally {
      // Don't reset isConnecting here to allow animation to complete
      // The component will unmount when onCompanySelected is called
    }
  };

  const handleOnboardingComplete = async (company: Company) => {
    setShowOnboarding(false);
    await loadData();
    onCompanySelected(company);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    window.location.reload();
  };

  const handleRetryConnection = () => {
    setError(null);
    loadData();
  };

  const handleArchiveCompany = async (company: Company) => {
    try {
      setIsLoading(true);
      setError(null);
      
      await updateCompany(company.id, { is_active: false });
      
      // Refresh companies list
      await loadData();
      
    } catch (err: any) {
      console.error('Error archiving company:', err);
      setError(`Failed to archive company: ${err.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnarchiveCompany = async (company: Company) => {
    try {
      setIsLoading(true);
      setError(null);
      
      await updateCompany(company.id, { is_active: true });
      
      // Refresh companies list
      await loadData();
      
    } catch (err: any) {
      console.error('Error unarchiving company:', err);
      setError(`Failed to unarchive company: ${err.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCompanyConfirm = async () => {
    if (!companyToDelete) return;
    
    try {
      setIsDeletingCompany(true);
      setError(null);
      
      // Delete the company from the database
      const { error: deleteError } = await supabase
        .from('companies')
        .delete()
        .eq('id', companyToDelete.id);
      
      if (deleteError) {
        throw new Error(`Database error: ${deleteError.message}`);
      }
      
      // Close the modal
      setIsDeleteModalOpen(false);
      setCompanyToDelete(null);
      
      // Refresh companies list
      await loadData();
      
    } catch (err: any) {
      console.error('Error deleting company:', err);
      setError(`Failed to delete company: ${err.message || 'Unknown error'}`);
    } finally {
      setIsDeletingCompany(false);
    }
  };

  const filteredCompanies = companies.filter(company => 
    showArchivedCompanies ? !company.is_active : company.is_active
  );

  if (showOnboarding) {
    return (
      <CompanyOnboarding 
        onComplete={handleOnboardingComplete}
        onCancel={() => setShowOnboarding(false)}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-jaimy-red border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading companies...</p>
          <div className="flex items-center justify-center gap-2 mt-2">
            {connectionStatus === 'connected' && <Wifi className="h-4 w-4 text-green-600" />}
            {connectionStatus === 'disconnected' && <WifiOff className="h-4 w-4 text-red-600" />}
            <span className="text-xs text-gray-500">
              {connectionStatus === 'connected' && 'Connected to Supabase'}
              {connectionStatus === 'disconnected' && 'Connection failed'}
              {connectionStatus === 'unknown' && 'Connecting...'}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl p-8">
        <div className="text-center mb-8">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
            isMagicAdminUser 
              ? 'bg-gradient-to-br from-purple-500 to-purple-600' 
              : 'bg-jaimy-red'
          }`}>
            {isMagicAdminUser ? (
              <Crown className="h-8 w-8 text-white" />
            ) : (
              <Building2 className="h-8 w-8 text-white" />
            )}
          </div>
          
          {isMagicAdminUser ? (
            <>
              <div className="flex items-center justify-center gap-2 mb-2">
                <Sparkles className="h-5 w-5 text-purple-600" />
                <h1 className="text-2xl font-bold text-gray-900">Welcome, Magic Admin!</h1>
                <Sparkles className="h-5 w-5 text-purple-600" />
              </div>
              <p className="text-gray-600 mb-4">
                With your superpowers, you can create as many new company accounts as you want and control all different company accounts via this login.
              </p>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-center gap-2 text-purple-800">
                  <Crown className="h-5 w-5" />
                  <span className="font-medium">Magic Admin Privileges</span>
                </div>
                <ul className="text-sm text-purple-700 mt-2 space-y-1">
                  <li>• Create unlimited company accounts</li>
                  <li>• Access all existing companies</li>
                  <li>• Manage subscriptions and billing</li>
                  <li>• Full administrative control</li>
                </ul>
              </div>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Select Your Company</h1>
              <p className="text-gray-600">Choose the company you want to access</p>
            </>
          )}
          
          {currentUser && (
            <div className="mt-2 flex items-center justify-center gap-2">
              <p className="text-sm text-gray-500">
                Logged in as: {currentUser.email}
              </p>
              {isMagicAdminUser && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  <Crown className="h-3 w-3 mr-1" />
                  Magic Admin
                </span>
              )}
            </div>
          )}

          {/* Connection Status Indicator */}
          <div className="mt-3 flex items-center justify-center gap-2">
            {connectionStatus === 'connected' && (
              <>
                <Wifi className="h-4 w-4 text-green-600" />
                <span className="text-xs text-green-600 font-medium">Connected to Supabase</span>
              </>
            )}
            {connectionStatus === 'disconnected' && (
              <>
                <WifiOff className="h-4 w-4 text-red-600" />
                <span className="text-xs text-red-600 font-medium">Connection failed</span>
              </>
            )}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-red-800 font-medium">Connection Error</p>
                <div className="text-red-700 text-sm mt-1 whitespace-pre-line">{error}</div>
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={handleRetryConnection}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm flex items-center gap-2"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Retry Connection
                  </button>
                  <button
                    onClick={() => setError(null)}
                    className="text-red-600 hover:text-red-800 px-4 py-2 text-sm"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Magic Admin Actions */}
        {isMagicAdminUser && connectionStatus === 'connected' && (
          <div className="mb-6 p-6 bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 rounded-lg">
            <h3 className="text-lg font-semibold text-purple-900 mb-3 flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              Magic Admin Actions
            </h3>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setShowOnboarding(true)}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-3 rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all duration-200 flex items-center gap-2 shadow-lg"
              >
                <Plus className="h-5 w-5" />
                Create New Company
              </button>
              <button
                onClick={loadData}
                disabled={isLoading}
                className="px-4 py-2 border border-purple-300 text-purple-700 rounded-lg hover:bg-purple-50 transition-colors flex items-center gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh Companies
              </button>
              <button
                onClick={() => setShowArchivedCompanies(!showArchivedCompanies)}
                className={`px-4 py-2 border ${showArchivedCompanies ? 'border-indigo-400 bg-indigo-50 text-indigo-700' : 'border-purple-300 text-purple-700'} rounded-lg hover:bg-purple-50 transition-colors flex items-center gap-2`}
              >
                <Archive className="h-4 w-4" />
                {showArchivedCompanies ? 'Show Active Companies' : 'Show Archived Companies'}
              </button>
            </div>
          </div>
        )}

        {connectionStatus === 'connected' && companies.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Building2 className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Companies Found</h3>
            <p className="text-gray-600 mb-6">
              {isMagicAdminUser 
                ? 'No companies exist yet. Create your first company to get started!'
                : 'No companies are available for your account.'
              }
            </p>
            {isMagicAdminUser && (
              <button
                onClick={() => setShowOnboarding(true)}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-8 py-3 rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all duration-200 flex items-center gap-2 mx-auto shadow-lg"
              >
                <Plus className="h-5 w-5" />
                Create First Company
              </button>
            )}
          </div>
        ) : connectionStatus === 'connected' && filteredCompanies.length > 0 ? (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">
                {showArchivedCompanies 
                  ? `Archived Companies (${filteredCompanies.length})` 
                  : `Available Companies (${filteredCompanies.length})`}
              </h2>
              {isMagicAdminUser && (
                <button
                  onClick={() => setShowOnboarding(true)}
                  className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 py-2 rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all duration-200 flex items-center gap-2 text-sm"
                >
                  <Plus className="h-4 w-4" />
                  Add New
                </button>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredCompanies.map((company) => (
                <div
                  key={company.id}
                  className={`p-6 border rounded-lg transition-all duration-200 text-left ${
                    showArchivedCompanies
                      ? 'border-gray-200 bg-gray-50'
                      : 'border-gray-200 hover:border-jaimy-red hover:bg-jaimy-red/5 cursor-pointer'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div 
                        className="h-12 w-12 rounded-lg flex items-center justify-center text-white shadow-sm"
                        style={{ backgroundColor: company.branding.primary_color }}
                      >
                        {company.branding.logo_url ? (
                          <img 
                            src={company.branding.logo_url} 
                            alt={company.name}
                            className="h-8 w-8 object-contain"
                          />
                        ) : (
                          <Building2 className="h-6 w-6" />
                        )}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {company.name}
                        </h3>
                        <p className="text-sm text-gray-600 capitalize">
                          {company.industry} • {company.subscription_tier} plan
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {company.features?.filter(f => f.is_enabled).length || 0} features enabled
                        </p>
                      </div>
                    </div>
                    
                    {showArchivedCompanies ? (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleUnarchiveCompany(company)}
                          className="text-gray-500 hover:text-green-600 transition-colors"
                          title="Unarchive Company"
                        >
                          <Archive className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => {
                            setCompanyToDelete(company);
                            setIsDeleteModalOpen(true);
                          }}
                          className="text-gray-500 hover:text-red-600 transition-colors"
                          title="Delete Company"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        {isMagicAdminUser && (
                          <>
                            <button
                              onClick={() => handleArchiveCompany(company)}
                              className="text-gray-400 hover:text-gray-600 transition-colors"
                              title="Archive Company"
                            >
                              <Archive className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => {
                                setCompanyToDelete(company);
                                setIsDeleteModalOpen(true);
                              }}
                              className="text-gray-400 hover:text-red-600 transition-colors"
                              title="Delete Company"
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleCompanySelect(company)}
                          disabled={isConnecting}
                          className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                            selectedCompanyId === company.id
                              ? 'bg-green-500 text-white scale-110'
                              : 'text-gray-400 hover:text-green-500 hover:bg-green-50'
                          }`}
                        >
                          {isConnecting && selectedCompanyId === company.id ? (
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <CheckCircle2 
                              className={`h-6 w-6 transition-all duration-300 ${
                                selectedCompanyId === company.id
                                  ? 'animate-[pulse_0.5s_ease-in-out]'
                                  : ''
                              }`} 
                            />
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : null}

        <div className="mt-8 pt-6 border-t border-gray-100 text-center">
          <button
            onClick={handleSignOut}
            className="text-gray-500 hover:text-gray-700 text-sm transition-colors flex items-center gap-2 mx-auto"
          >
            <LogOut className="h-4 w-4" />
            Sign out and use different account
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && companyToDelete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-6">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-red-100 text-red-600 mx-auto mb-4 flex items-center justify-center">
                <AlertCircle className="h-6 w-6" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Delete Company</h2>
              <p className="text-gray-600 mb-6">
                Are you sure you want to permanently delete <span className="font-medium">{companyToDelete.name}</span>? This action cannot be undone and will erase all company data including users, locations, bookings, and invoices.
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => {
                    setIsDeleteModalOpen(false);
                    setCompanyToDelete(null);
                  }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteCompanyConfirm}
                  disabled={isDeletingCompany}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {isDeletingCompany ? (
                    <>
                      <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4" />
                      Delete Company
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}